import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AssignmentFilters = ({ onFilterChange, onSavePreset, savedPresets }) => {
  const [filters, setFilters] = useState({
    searchQuery: '',
    assignmentType: '',
    status: '',
    roleType: '',
    dateRange: '',
    driverIds: [],
    unitIds: [],
    yardIds: []
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  const assignmentTypeOptions = [
    { value: '', label: 'All Assignment Types' },
    { value: 'driver-unit', label: 'Driver-Unit Assignment' },
    { value: 'unit-yard', label: 'Unit-Yard Assignment' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'expired', label: 'Expired' },
    { value: 'upcoming', label: 'Upcoming' }
  ];

  const roleTypeOptions = [
    { value: '', label: 'All Roles' },
    { value: 'primary', label: 'Primary Driver' },
    { value: 'relief', label: 'Relief Driver' }
  ];

  const dateRangeOptions = [
    { value: '', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: 'this-week', label: 'This Week' },
    { value: 'this-month', label: 'This Month' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const driverOptions = [
    { value: 'D001', label: 'James Wilson' },
    { value: 'D002', label: 'Sarah Martinez' },
    { value: 'D003', label: 'Michael Chen' },
    { value: 'D004', label: 'Emily Rodriguez' },
    { value: 'D005', label: 'David Thompson' }
  ];

  const unitOptions = [
    { value: 'U001', label: 'Unit 1001 - Freightliner' },
    { value: 'U002', label: 'Unit 1002 - Kenworth' },
    { value: 'U003', label: 'Unit 1003 - Peterbilt' },
    { value: 'U004', label: 'Unit 1004 - Volvo' },
    { value: 'U005', label: 'Unit 1005 - Mack' }
  ];

  const yardOptions = [
    { value: 'Y001', label: 'Main Distribution Center' },
    { value: 'Y002', label: 'North Terminal' },
    { value: 'Y003', label: 'South Depot' },
    { value: 'Y004', label: 'East Facility' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      searchQuery: '',
      assignmentType: '',
      status: '',
      roleType: '',
      dateRange: '',
      driverIds: [],
      unitIds: [],
      yardIds: []
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const handleSavePreset = () => {
    if (presetName?.trim()) {
      onSavePreset(presetName, filters);
      setPresetName('');
      setShowSavePreset(false);
    }
  };

  const activeFilterCount = Object.values(filters)?.filter(value => 
    Array.isArray(value) ? value?.length > 0 : value !== ''
  )?.length;

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Icon name="Filter" size={20} color="var(--color-primary)" strokeWidth={2} />
            <h3 className="text-lg font-semibold text-foreground">Assignment Filters</h3>
            {activeFilterCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-md">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="RotateCcw"
              onClick={handleClearFilters}
            >
              Clear All
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName={showAdvanced ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              Advanced
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            type="search"
            placeholder="Search assignments..."
            value={filters?.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
            className="col-span-1 md:col-span-2"
          />

          <Select
            placeholder="Assignment Type"
            options={assignmentTypeOptions}
            value={filters?.assignmentType}
            onChange={(value) => handleFilterChange('assignmentType', value)}
          />

          <Select
            placeholder="Status"
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
          />
        </div>

        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select
                placeholder="Role Type"
                options={roleTypeOptions}
                value={filters?.roleType}
                onChange={(value) => handleFilterChange('roleType', value)}
              />

              <Select
                placeholder="Date Range"
                options={dateRangeOptions}
                value={filters?.dateRange}
                onChange={(value) => handleFilterChange('dateRange', value)}
              />

              <Select
                placeholder="Select Drivers"
                options={driverOptions}
                value={filters?.driverIds}
                onChange={(value) => handleFilterChange('driverIds', value)}
                multiple
                searchable
                clearable
              />

              <Select
                placeholder="Select Units"
                options={unitOptions}
                value={filters?.unitIds}
                onChange={(value) => handleFilterChange('unitIds', value)}
                multiple
                searchable
                clearable
              />

              <Select
                placeholder="Select Yards"
                options={yardOptions}
                value={filters?.yardIds}
                onChange={(value) => handleFilterChange('yardIds', value)}
                multiple
                searchable
                clearable
              />
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="Save" size={16} color="var(--color-muted-foreground)" strokeWidth={2} />
                <span className="text-sm text-muted-foreground">Saved Presets:</span>
                {savedPresets?.map((preset, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      setFilters(preset?.filters);
                      onFilterChange(preset?.filters);
                    }}
                  >
                    {preset?.name}
                  </Button>
                ))}
              </div>

              {!showSavePreset ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Plus"
                  onClick={() => setShowSavePreset(true)}
                >
                  Save Current Filters
                </Button>
              ) : (
                <div className="flex items-center space-x-2">
                  <Input
                    type="text"
                    placeholder="Preset name..."
                    value={presetName}
                    onChange={(e) => setPresetName(e?.target?.value)}
                    className="w-48"
                  />
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Check"
                    onClick={handleSavePreset}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => {
                      setShowSavePreset(false);
                      setPresetName('');
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentFilters;